import React, { useState } from "react"

import Image from "next/image"
import { capitalize, isEmpty } from "lodash"
import { useTranslation } from "react-i18next"
import { AnimatePresence, motion } from "framer-motion"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faChevronRight, faChevronUp, faPlus } from "@fortawesome/free-solid-svg-icons"

import { MediumText } from "@/UI"
import KosmoButton from "@/UI/Buttons/KosmoButton"
import EcommerceShopsList from "./EcommerceShopsList"

import { KOSMO_SHOPIFY_APP_LINK } from "@/constants/routes"
import { EcommerceIntegration, PlatformConfig } from "@/types/ecommerce"

interface EcommercePlatformsListProps {
  config: PlatformConfig
  ecommerceIntegrations?: EcommerceIntegration[]
  setEcommerceIntegrations: (integration: EcommerceIntegration[]) => void
  setStoreFocus: any
}

const EcommercePlatformsList: React.FC<EcommercePlatformsListProps> = ({
  config,
  ecommerceIntegrations,
  setEcommerceIntegrations,
  setStoreFocus,
}) => {
  const { t } = useTranslation()
  const [isExpanded, setIsExpanded] = useState<boolean>(!isEmpty(ecommerceIntegrations))

  return (
    <div id="ecommerce-platform-card" className="flex flex-col space-y-4">
      <div className="flex justify-between items-center select-none">
        <div className="flex gap-x-4 items-center">
          <Image src={config.icon} width={40} height={40} priority={true} />
          <div className="flex flex-col justify-between">
            <MediumText>{capitalize(config.platform)}</MediumText>
            {!config.active && <p>{t("common.comingSoon")}!</p>}
          </div>
        </div>
        {config.active && (
          <div className="flex items-center gap-x-6">
            <a href={KOSMO_SHOPIFY_APP_LINK} target="_blank" rel="noopener noreferrer">
              <KosmoButton id="ecommerce-add-new-store-button" type="primary">
                <div className="flex items-center gap-x-2">
                  <FontAwesomeIcon icon={faPlus} color="#FFF" />
                  <p>{t("profile.EcommerceSettings.addNewStore")}</p>
                </div>
              </KosmoButton>
            </a>
            {!isEmpty(ecommerceIntegrations) ? (
              <FontAwesomeIcon
                id="open-ecommerce-integrations-list"
                onClick={() => !isEmpty(ecommerceIntegrations) && setIsExpanded(!isExpanded)}
                className="flex-shrink-0 cursor-pointer w-3 h-3"
                icon={isExpanded ? faChevronUp : faChevronRight}
              />
            ) : (
              <div className="w-3 h-3"></div>
            )}
          </div>
        )}
      </div>
      <AnimatePresence>
        {!isEmpty(ecommerceIntegrations) &&
          ecommerceIntegrations &&
          isExpanded &&
          config.active && (
            <motion.div
              className="!mt-6 overflow-hidden gap-y-2 flex flex-col h-full w-full"
              exit="exit"
              transition={{
                opacity: { duration: 0.5 },
                duration: 0.3,
              }}
              initial={{
                zIndex: -1000,
                y: -100,
                opacity: 0,
              }}
              animate={{
                zIndex: 1,
                y: 0,
                opacity: 1,
              }}
            >
              <EcommerceShopsList
                ecommerceIntegrations={ecommerceIntegrations}
                setEcommerceIntegrations={setEcommerceIntegrations}
                setStoreFocus={setStoreFocus}
              />
            </motion.div>
          )}
      </AnimatePresence>
    </div>
  )
}

export default EcommercePlatformsList
